import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import moment from "moment";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import { EmptyState, PageHeader } from "../../components/dashboard/DashboardUI";
import { getAllCompanies } from "../../redux/actions/CompanyActions";
import { getCompanyValidSubscriptions } from "../../redux/actions/SubscriptionActions";
import { SERVICE_NAMES } from "../../utils/requestDisplay";
import { ACTIVITY_SECTORS } from "../../utils/requestDetails";
import { formatPrice } from "../../utils/formatPrice";

countries.registerLocale(fr);

const PAGE_SIZE = 20;

const countryName = (code) => countries.getName(code, "fr") || code || "—";
const ownerName = (company) =>
  [company.user?.firstname, company.user?.lastname].filter(Boolean).join(" ");
const formatDate = (date) => (date ? moment(date).format("DD/MM/YYYY") : "—");

const Details = ({ rows }) => (
  <dl className="dash-details">
    {rows
      .filter(([, value]) => value)
      .map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
  </dl>
);

// Companies of the clients, with their owner and active services
function OperatorCompanies() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { userCompanies } = useSelector((state) => state.companies);
  const { companyValidSubscriptions } = useSelector(
    (state) => state.subscriptions,
  );

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState(null);
  const [loadingServices, setLoadingServices] = useState(false);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.resolve(dispatch(getAllCompanies(user.token))).finally(() =>
      setLoading(false),
    );
  }, [user?.token, dispatch]);

  const open = (company) => {
    setSelected(company);
    setLoadingServices(true);
    Promise.resolve(
      dispatch(getCompanyValidSubscriptions(user.token, company._id)),
    ).finally(() => setLoadingServices(false));
  };

  const term = search.trim().toLowerCase();
  const companies = (userCompanies || []).filter(
    (company) =>
      !term ||
      [
        company.businessName,
        company.businessType,
        ownerName(company),
        countryName(company.country),
        company.mobile,
        company.user?.email,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term)),
  );

  return (
    <>
      <PageHeader
        title="Entreprises"
        lead={`${(userCompanies || []).length} entreprises clientes.`}
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une entreprise, un propriétaire, un pays…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setVisible(PAGE_SIZE);
            }}
            aria-label="Rechercher"
          />
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des entreprises…</p>
        ) : companies.length === 0 ? (
          <EmptyState title="Aucune entreprise">
            Aucune entreprise ne correspond à votre recherche.
          </EmptyState>
        ) : (
          <>
            <div className="dash-table-wrap">
              <table className="dash-table is-responsive">
                <thead>
                  <tr>
                    <th>Entreprise</th>
                    <th>Forme</th>
                    <th>Propriétaire</th>
                    <th>Pays</th>
                    <th>Téléphone</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {companies.slice(0, visible).map((company) => (
                    <tr key={company._id}>
                      <td>
                        <span className="dash-cell-title">
                          {company.businessName || "Sans nom"}
                        </span>
                      </td>
                      <td data-label="Forme">{company.businessType}</td>
                      <td data-label="Propriétaire">
                        {ownerName(company) || "—"}
                      </td>
                      <td data-label="Pays">{countryName(company.country)}</td>
                      <td data-label="Téléphone">{company.mobile || "—"}</td>
                      <td>
                        <div className="dash-row-actions">
                          <button
                            type="button"
                            className="dash-link-button"
                            onClick={() => open(company)}
                          >
                            Voir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {companies.length > visible && (
              <div className="dash-more">
                <button
                  type="button"
                  className="dash-button is-secondary"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                >
                  Afficher plus
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        show={Boolean(selected)}
        onHide={() => setSelected(null)}
        size="lg"
        centered
      >
        {selected && (
          <>
            <Modal.Header closeButton>
              <div className="dash-modal-head">
                <Modal.Title as="h2" className="dash-card-title mb-0">
                  {selected.businessName || "Sans nom"}
                </Modal.Title>
                <span className="dash-cell-sub">
                  {selected.businessType} · client depuis le{" "}
                  {formatDate(selected.createdAt)}
                </span>
              </div>
            </Modal.Header>
            <Modal.Body>
              <h3 className="dash-subtitle">Entreprise</h3>
              <Details
                rows={[
                  [
                    "Secteur d'activité",
                    ACTIVITY_SECTORS[selected.activitySector] ||
                      selected.activitySector,
                  ],
                  [
                    "Chiffre d'affaires",
                    selected.turnover && formatPrice(selected.turnover),
                  ],
                  ["Pays", countryName(selected.country)],
                  ["Siège social", selected.address],
                  ["Téléphone", selected.mobile],
                  ["Fixe", selected.fix],
                  ["WhatsApp", selected.whatsApp],
                  ["E-mail", selected.email],
                ]}
              />

              <h3 className="dash-subtitle">Propriétaire</h3>
              <Details
                rows={[
                  ["Nom", ownerName(selected)],
                  ["E-mail", selected.user?.email],
                  ["Téléphone", selected.user?.mobile],
                  ["WhatsApp", selected.user?.whatsApp],
                  ["Pays de résidence", countryName(selected.user?.country)],
                ]}
              />

              <h3 className="dash-subtitle">Services actifs</h3>
              {loadingServices ? (
                <p className="dash-loading">Chargement…</p>
              ) : companyValidSubscriptions?.length ? (
                <ul className="dash-list">
                  {companyValidSubscriptions.map((sub, index) => (
                    <li key={sub._id || index}>
                      <span className="dash-cell-title">
                        {SERVICE_NAMES[sub.service] || sub.service}
                      </span>
                      <span className="dash-cell-sub">
                        {formatDate(sub.startsOn)} →{" "}
                        {sub.endsOn ? formatDate(sub.endsOn) : "sans fin"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="dash-page-lead">
                  Aucun service actif pour cette entreprise.
                </p>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  );
}

export default OperatorCompanies;
